import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { FlyoutQuickAction } from '@/experiences/sales-portal/components/quick-action-menu';
import { Icons } from '@usepepper/sprout-kit-ui';
import { OverrideExpirationPicker } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/override-expiration-picker';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { useTheme } from '@/contexts/theme-context';

const TEMPORARY_ADJUSTMENT_ICON_SIZE = 16;

// The expiry is a plain calendar date (YYYY-MM-DD) with no timezone.
// It's converted via local date components rather than moment to prevent
// moment from rebasing to the restaurant's timezone causing a shift in date
const parseExpirationDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatExpirationDate = (date: Date): string =>
  [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

interface UseTemporaryAdjustmentQuickActionParams {
  editor: PriceEditor | undefined;
  hasActiveOverride: boolean;
  isDirty: boolean;
  isEnabled: boolean;
}

/**
 * Builds the "Temporary adjustment" quick action shared by the price and GP%
 * cells. Apply/Remove only stage the expiry - the save happens when the menu
 * closes, on the same path as every other edit.
 *
 * @returns The quick action, or null when disabled or the editor is absent.
 */
export const useTemporaryAdjustmentQuickAction = ({
  editor,
  hasActiveOverride,
  isDirty,
  isEnabled,
}: UseTemporaryAdjustmentQuickActionParams): FlyoutQuickAction | null => {
  const theme = useTheme();

  return useMemo(() => {
    if (!isEnabled || !editor) {
      return null;
    }

    return {
      icon: (
        <Icons.Calendar
          color={theme.sk.color.text.primary}
          size={TEMPORARY_ADJUSTMENT_ICON_SIZE}
        />
      ),
      key: 'temporary-adjustment',
      label: i18n.t(
        'sales_portal.order_entry.quick_actions.temporary_adjustment',
      ),
      renderFlyout: ({ closeMenu }) => {
        const savedExpiration = editor.getSavedExpirationDate();

        return (
          <OverrideExpirationPicker
            canAdjust={isDirty || hasActiveOverride}
            initialExpiryDate={
              savedExpiration ? parseExpirationDate(savedExpiration) : null
            }
            onApply={(date) => {
              editor.setExpirationDate(formatExpirationDate(date));
              closeMenu();
            }}
            onRemove={() => {
              editor.setExpirationDate(null);
              closeMenu();
            }}
            showRemove={savedExpiration !== null}
          />
        );
      },
    };
  }, [editor, hasActiveOverride, isDirty, isEnabled, theme]);
};
